import { Card, CardContent, CardHeader, CardFooter } from "@/components/ui/card";
import { Loan } from "@/types/borrower";
import { useIsMobile } from "@/hooks/use-mobile";
import { Building2, Mail, MessageCircle, Phone, User, Shield, Check, AlertCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { formatCurrency } from "@/utils/currencyUtils";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { 
  Tooltip, 
  TooltipContent, 
  TooltipProvider, 
  TooltipTrigger 
} from "@/components/ui/tooltip";
import { useToast } from "@/components/ui/use-toast";

interface BorrowerCardProps {
  loan: Loan;
}

export const BorrowerCard = ({ loan }: BorrowerCardProps) => {
  const isMobile = useIsMobile();
  const isBusinessLoan = loan.category === 'business';
  const { toast } = useToast();
  const [isContactVisible, setIsContactVisible] = useState(false);

  const hasVerification = loan.collateral?.blockchain_verification;
  
  const handleContact = (method: 'email' | 'phone' | 'message') => {
    switch (method) {
      case 'email':
        if (loan.borrower?.profile?.email) {
          window.location.href = `mailto:${loan.borrower.profile.email}`;
        } else {
          toast({
            title: "Contact information unavailable",
            description: "Email address is not available for this borrower.",
            variant: "destructive",
          });
        }
        break;
      case 'phone':
        if (loan.borrower?.profile?.phone_number) {
          window.location.href = `tel:${loan.borrower.profile.phone_number}`;
        } else {
          toast({
            title: "Contact information unavailable",
            description: "Phone number is not available for this borrower.",
            variant: "destructive",
          });
        }
        break;
      case 'message':
        toast({
          title: "Message feature",
          description: "Direct messaging feature will be available soon.",
        });
        break;
      default:
        break;
    }
  };

  return (
    <Card className="hover:shadow-lg transition-shadow duration-300 hover:scale-[1.02]">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-center">
          <Badge variant={isBusinessLoan ? "default" : "outline"} className="px-3 py-1">
            {isBusinessLoan ? (
              <div className="flex items-center gap-1">
                <Building2 className="h-3.5 w-3.5 mr-1" />
                Business
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <User className="h-3.5 w-3.5 mr-1" />
                Personal
              </div>
            )}
          </Badge>
          <Badge 
            variant="outline" 
            className={`px-3 py-1 ${
              (loan.funding_progress / loan.amount) * 100 >= 50 
                ? "bg-green-100 text-green-800 hover:bg-green-100" 
                : "bg-yellow-100 text-yellow-800 hover:bg-yellow-100"
            }`}
          >
            {((loan.funding_progress / loan.amount) * 100).toFixed(0)}% Funded
          </Badge>
        </div>
      </CardHeader>
      <CardContent className={`${isMobile ? 'p-3' : 'p-4 lg:p-6'} space-y-4 lg:space-y-6`}>
        <div className="space-y-3">
          {isBusinessLoan ? (
            <>
              <div className="flex justify-between items-start">
                <span className="text-sm text-muted-foreground font-medium">Company Name:</span>
                <span className="text-sm font-medium text-right">
                  {loan.borrower?.profile?.business_name || `Business #${loan.id.slice(0, 6)}`}
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="flex justify-between items-start">
                <span className="text-sm text-muted-foreground font-medium">Name:</span>
                <span className="text-sm font-medium text-right">
                  {loan.borrower?.profile?.first_name} {loan.borrower?.profile?.last_name}
                </span>
              </div>
            </>
          )}
          
          <div className="flex justify-between items-start">
            <span className="text-sm text-muted-foreground font-medium">Purpose of Loan:</span>
            <span className="text-sm font-medium text-right max-w-[60%] line-clamp-2">
              {loan.purpose || "Not specified"}
            </span>
          </div>
          
          <div className="flex justify-between items-start">
            <span className="text-sm text-muted-foreground font-medium">Amount Required:</span>
            <span className="text-sm font-medium text-right">
              {formatCurrency(loan.amount, "GHS")}
            </span>
          </div>
          
          <div className="flex justify-between items-start">
            <span className="text-sm text-muted-foreground font-medium">Tenor:</span>
            <span className="text-sm font-medium text-right">
              {loan.funding_deadline 
                ? new Date(loan.funding_deadline).toLocaleDateString()
                : "No deadline"}
            </span>
          </div>
          
          <div className="flex justify-between items-start">
            <span className="text-sm text-muted-foreground font-medium">Guarantee Level:</span>
            <span className="text-sm font-medium text-right">
              100% Guaranteed by Obotan
            </span>
          </div>
        </div>
        
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground font-medium">Brief Description of Loan:</p>
          <p className="text-sm line-clamp-3 border border-gray-200 rounded-md p-2 bg-gray-50">
            {loan.story || "No description provided"}
          </p>
        </div>
        
        {loan.collateral && (
          <div className="flex justify-between items-start">
            <span className="text-sm text-muted-foreground font-medium">Property Collateral:</span>
            <div className="flex flex-col items-end">
              <span className="text-sm font-medium text-right">
                {formatCurrency(loan.collateral.collateral_value, "GHS")}
              </span>
              {hasVerification && (
                <div className="flex items-center mt-1">
                  {hasVerification.verification_status === 'verified' ? (
                    <Badge variant="outline" className="bg-green-50 text-green-800 border-green-200 text-xs flex items-center gap-1">
                      <Check className="h-3 w-3" />
                      Blockchain Verified
                    </Badge>
                  ) : hasVerification.verification_status === 'pending' ? (
                    <Badge variant="outline" className="bg-yellow-50 text-yellow-800 border-yellow-200 text-xs flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      Verification Pending
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-red-50 text-red-800 border-red-200 text-xs flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      Verification Failed
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
      <CardFooter className="px-4 lg:px-6 pt-0 pb-4 flex justify-between items-center">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => setIsContactVisible(!isContactVisible)}
          className="text-primary"
        >
          Contact Borrower
        </Button>
        
        {isContactVisible && (
          <div className={`flex ${isMobile ? 'space-x-2' : 'space-x-2'}`}>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={() => handleContact('email')}
                    className="rounded-full bg-white text-blue-600 border-blue-200 hover:bg-blue-50"
                  >
                    <Mail className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Email</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={() => handleContact('phone')}
                    className="rounded-full bg-white text-green-600 border-green-200 hover:bg-green-50"
                  >
                    <Phone className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Call</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={() => handleContact('message')}
                    className="rounded-full bg-white text-purple-600 border-purple-200 hover:bg-purple-50"
                  >
                    <MessageCircle className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Message</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
      </CardFooter>
    </Card>
  );
};
